import { Button } from '@/components/ui/Button'
import { useMobile } from '@/hooks/useMobile'
import { classes } from '@lc/libreact'
import {
	useRef,
	type KeyboardEvent,
	type MouseEvent,
	type ReactNode,
	type RefObject,
} from 'react'
import { ButtonCloseModalPopup } from './ButtonCloseModalPopup'
import styles from './Modal.scss'

type ModalProps = {
	onClose: () => void
	refToAlign?: RefObject<HTMLButtonElement | null>
	children: ReactNode
	closePicto?: string
	fullModal?: boolean
	streetViewModal?: boolean
	mapOptionsModal?: boolean
	className?: string
}

// TODO Maybe "dialog" isn't the best element to use here (a11y)
export function Modal({
	onClose,
	refToAlign,
	children,
	closePicto,
	fullModal,
	streetViewModal = false,
	mapOptionsModal = false,
	className,
}: ModalProps) {
	const ref = useRef<HTMLDialogElement | null>(null)
	const mobile = useMobile()

	const handleClick = (e: MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (e.target === ref?.current) {
			onClose()
		}
	}

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Escape') {
			e.preventDefault()
			e.stopPropagation()
			onClose()
		}
	}

	return (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<dialog
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			ref={e => {
				if (!e) return
				ref.current = e

				// Align the modal with the button that opened it on desktop
				// Disable this for streetViewModal
				if (!mobile && refToAlign && !streetViewModal) {
					const rect = refToAlign.current?.getBoundingClientRect()

					if (rect) {
						ref.current.style.setProperty(
							'--lc-position-modal-top',
							`${rect.top}px`,
						)
						ref.current.style.setProperty(
							'--lc-height-modal',
							`calc(100vh - ${rect.top}px - var(--lc-position-board-top))`,
						)
					}
				}

				if (!e.open) e.showModal()
			}}
			className={classes(
				styles.modalContent,
				[styles.fullModalContent, fullModal],
				[styles.modalStreetView, streetViewModal],
				[styles.modalMapOptions, mapOptionsModal],
				className,
			)}
		>
			{!fullModal && (
				<ButtonCloseModalPopup
					onClose={onClose}
					closePicto={closePicto}
					isModal
				/>
			)}

			<div
				className={classes(styles.modalChildren, [
					styles.fullModalChildren,
					fullModal,
				])}
			>
				{fullModal && (
					<Button
						className={styles.backButton}
						onClick={onClose}
						iconUrl={closePicto ?? 'assets/Cross.svg'}
						aria-label="Quitter la fenêtre modale"
					/>
				)}
				{children}
			</div>
		</dialog>
	)
}
